import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Menu, X, ChevronDown, ShieldCheck, LogOut,
  LayoutDashboard, Package, Wallet, TreePine, Building2, FileText, Camera,
  BarChart2, Satellite, Leaf, Flame, Receipt
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../hooks/useCart';
import { useTheme } from '../context/ThemeContext';
import ShoppingCartDrawer from './ShoppingCart';
import { db } from '../config/firebase';
import { getDoc, doc } from 'firebase/firestore';

/* Grouped by audience journey — long flat lists don't scan; three labelled
   columns do. Colours match the Explore-map branches. */
const SERVICE_GROUPS = [
  {
    title: '🌱 Grow & Shop', color: '#2F9E5E',
    items: [
      { to: '/grow',           label: 'Growing Guides',       desc: 'County & crop agronomy' },
      { to: '/weather',        label: 'Weather & Rain',       desc: 'When to transplant' },
      { to: '/find-seedlings', label: 'Find My Seedlings',    desc: '4-tap planting plan' },
      { to: '/how-we-grow',    label: 'How We Grow',          desc: 'Inside the Molo nursery' },
      { to: '/track',          label: 'Track Your Seedlings', desc: 'Order & delivery status' },
      { to: '/fresh-produce',  label: 'Fresh Produce Export', desc: 'Chilli aggregation & export' },
    ],
  },
  {
    title: '🌳 Restore & ESG', color: '#13492F',
    items: [
      { to: '/sponsor-a-tree',    label: 'Sponsor a Tree',       desc: 'From KES 500, tracked' },
      { to: '/bulk-restoration',  label: 'Bulk & Restoration',   desc: 'Counties, NGOs, schools' },
      { to: '/corporate-esg',     label: 'Corporate ESG / CSR',  desc: 'MRV · TCFD · GRI 304-3' },
      { to: '/restoration-track', label: 'Restoration Tracking', desc: 'Survival, NDVI & impact' },
      { to: '/field-capture',     label: 'Field Capture',        desc: 'Agent planting records' },
    ],
  },
  {
    title: '✨ Discover', color: '#B5651D',
    items: [
      { to: '/explore',         label: 'Explore Mbegu',   desc: 'The whole map, in 3D' },
      { to: '/play',            label: 'Shamba Game',     desc: 'Keep 70% alive, earn credits' },
      { to: '/impact',          label: 'Impact',          desc: 'Our numbers and reach' },
      { to: '/company-profile', label: 'Company Profile', desc: 'Capabilities & certs' },
    ],
  },
];
const SERVICES = SERVICE_GROUPS.flatMap(g => g.items);

const Navbar: React.FC = () => {
  const { user, logout, signInWithGoogle } = useAuth();
  const { cartItems } = useCart();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [scrolled, setScrolled]       = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [userOpen, setUserOpen]       = useState(false);
  const [cartOpen, setCartOpen]       = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [isAdmin, setIsAdmin]         = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);

  const onHome = location.pathname === '/';

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 48);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setMobileOpen(false); setUserOpen(false); setServicesOpen(false); }, [location]);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) setServicesOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    const emails = (process.env.REACT_APP_ADMIN_EMAIL || '').split(',').map(e => e.trim());
    if (emails.includes(user.email || '')) { setIsAdmin(true); return; }
    getDoc(doc(db, 'admins', user.uid || user.id))
      .then(s => { if (s.exists()) { const d = s.data(); setIsAdmin(!d.role || d.role === 'admin'); } else setIsAdmin(false); })
      .catch(() => setIsAdmin(false));
  }, [user]);

  const transparent = onHome && !scrolled && !mobileOpen;
  const onServices = SERVICES.some(s => location.pathname === s.to);

  return (
    <>
      <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-200 ${
        transparent ? 'bg-transparent' : 'bg-white/95 dark:bg-gray-950/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800'
      }`}>
        <nav className="container mx-auto flex items-center justify-between h-16 px-4 max-w-6xl">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/images/mbegu-traders-logo.png"
              alt="Mbegu Commercial Partners & Traders Limited"
              className="h-10 w-auto object-contain"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-0.5">
            {[['Products', '/products'], ['About', '/about']].map(([l, to]) => (
              <Link key={to} to={to} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === to
                  ? 'text-primary bg-green-50 dark:bg-green-900/20'
                  : transparent
                    ? 'text-white/80 hover:text-white hover:bg-white/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}>{l}</Link>
            ))}

            {/* Services dropdown */}
            <div ref={servicesRef} className="relative">
              <button
                onClick={() => setServicesOpen(v => !v)}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  onServices
                    ? 'text-primary bg-green-50 dark:bg-green-900/20'
                    : transparent
                      ? 'text-white/80 hover:text-white hover:bg-white/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}>
                Services
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 w-[640px] max-w-[92vw] rounded-2xl p-4 z-50 bg-popover text-popover-foreground border border-border shadow-heavy">
                  <div className="grid grid-cols-3 gap-4">
                    {SERVICE_GROUPS.map(g => (
                      <div key={g.title}>
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] mb-2 px-2 flex items-center gap-1.5"
                          style={{ color: g.color }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: g.color }} />
                          {g.title}
                        </p>
                        {g.items.map(s => (
                          <Link key={s.to} to={s.to} onClick={() => setServicesOpen(false)}
                            className="block px-2 py-1.5 rounded-lg hover:bg-muted transition-colors">
                            <p className="text-[13px] font-semibold text-foreground leading-tight">{s.label}</p>
                            <p className="text-[11px] text-muted-foreground">{s.desc}</p>
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {[['Contact', '/contact']].map(([l, to]) => (
              <Link key={to} to={to} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === to
                  ? 'text-primary bg-green-50 dark:bg-green-900/20'
                  : transparent
                    ? 'text-white/80 hover:text-white hover:bg-white/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}>{l}</Link>
            ))}
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm transition-colors ${
                transparent ? 'text-white/70 hover:bg-white/10' : 'text-muted-foreground hover:bg-muted'
              }`}>
              {theme === 'light' ? '🌙' : '☀️'}
            </button>

            <button onClick={() => setCartOpen(true)}
              className={`relative w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                transparent ? 'text-white/70 hover:bg-white/10' : 'text-muted-foreground hover:bg-muted'
              }`}>
              <ShoppingCart className="w-4 h-4" />
              {cartItems.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {cartItems.length > 9 ? '9+' : cartItems.length}
                </span>
              )}
            </button>

            {user ? (
              <div className="relative">
                <button onClick={() => setUserOpen(v => !v)}
                  className="flex items-center gap-1.5 pr-2 pl-1 py-1 rounded-lg hover:bg-muted transition-colors">
                  <img src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=166534&color=fff`}
                    className="w-7 h-7 rounded-md object-cover" alt="" />
                  <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${userOpen ? 'rotate-180' : ''}`} />
                </button>
                {userOpen && (
                  <div className="absolute right-0 mt-1 w-52 rounded-xl py-1 z-50 bg-popover text-popover-foreground border border-border shadow-heavy">
                    <div className="px-3 py-2 border-b border-border mb-1">
                      <p className="font-semibold text-sm text-foreground truncate">{user.name || 'Farmer'}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    {[
                      ['/dashboard', <LayoutDashboard className="w-4 h-4" />, 'Dashboard'],
                      ['/orders',    <Package className="w-4 h-4" />,         'My Orders'],
                      ['/wallet',    <Wallet className="w-4 h-4" />,          'Wallet'],
                    ].map(([to, icon, label]) => (
                      <Link key={to as string} to={to as string} onClick={() => setUserOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                        {icon as React.ReactNode}{label as string}
                      </Link>
                    ))}
                    {isAdmin && (
                      <>
                        <div className="border-t border-border my-1" />
                        <Link to="/admin" onClick={() => setUserOpen(false)}
                          className="flex items-center gap-2.5 px-3 py-2 text-sm text-primary font-medium hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                          <ShieldCheck className="w-4 h-4" />Admin Panel
                        </Link>
                      </>
                    )}
                    <div className="border-t border-border my-1" />
                    <button onClick={async () => { setUserOpen(false); await logout(); navigate('/'); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <LogOut className="w-4 h-4" />Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={() => signInWithGoogle()}
                className={`btn btn-sm ${transparent ? 'bg-white text-primary hover:bg-green-50' : 'btn-primary'}`}>
                Sign In
              </button>
            )}

            <button onClick={() => setMobileOpen(v => !v)}
              className={`md:hidden w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                transparent ? 'text-white/70 hover:bg-white/10' : 'text-muted-foreground hover:bg-muted'
              }`}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white dark:bg-gray-950 border-t border-border px-4 py-3 space-y-0.5">
            {[['Products', '/products'], ['About', '/about']].map(([l, to]) => (
              <Link key={to} to={to}
                className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === to ? 'bg-green-50 text-primary dark:bg-green-900/20' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}>{l}</Link>
            ))}
            <div className="border-t border-border my-1 pt-1">
              {SERVICE_GROUPS.map(g => (
                <div key={g.title}>
                  <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: g.color }}>
                    {g.title}
                  </p>
                  {g.items.map(s => (
                    <Link key={s.to} to={s.to}
                      className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        location.pathname === s.to ? 'bg-green-50 text-primary dark:bg-green-900/20' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}>{s.label}</Link>
                  ))}
                </div>
              ))}
            </div>
            <div className="border-t border-border my-1 pt-1">
              <Link to="/contact" className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === '/contact' ? 'bg-green-50 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}>Contact</Link>
            </div>
            {user && (
              <>
                <div className="border-t border-border my-1 pt-1" />
                {[['/dashboard','Dashboard'],['/orders','My Orders'],['/wallet','Wallet']].map(([to, l]) => (
                  <Link key={to} to={to} className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted">{l}</Link>
                ))}
                {isAdmin && <Link to="/admin" className="block px-3 py-2.5 rounded-lg text-sm font-medium text-primary">Admin Panel</Link>}
              </>
            )}
          </div>
        )}
      </header>

      <ShoppingCartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
};

export default Navbar;
